/**
 * wristStation — ADR-025: **the hand joint is evidence about the wrist, never the
 * wrist itself.** Three pure functions, one responsibility each:
 *
 *   proposeWristStation  — measure where the wrist crease IS along the arm (a
 *                          GHOST proposal, D1) or say it cannot (bracers, cuffs)
 *   wristStationMove     — turn a confirmed station (a clicked SURFACE point) into
 *                          the joint's new position: the SAME lateral place, moved
 *                          ALONG the arm only — the one axis the tier-2 recentre
 *                          deliberately never touches (limb-axis-centre)
 *   reownedVertices      — the palm root: flesh between the station and the old
 *                          joint that Tripo weighted to the severed forearm chain
 *                          because it painted hand weight from wherever it dropped
 *                          the joint (measured 2026-08-15: batch flesh starts at
 *                          0%L of the joint; Jett's at +4–10%L)
 *
 * WHY THE CLICK'S ALONG-AXIS COORDINATE AND NOT THE CLICK. A click lands on the
 * skin. The wrist is the tube's centre at that station, and the lateral recentre
 * that already runs (limbAxisCentre) is the instrument for the centre — so the
 * station contributes exactly the one number the recentre refuses to change, and
 * the two compose instead of competing.
 *
 * WHY MEASUREMENT PROPOSES AND A PERSON ANSWERS (D1). A min-girth search along a
 * bare arm finds the crease; under a bracer or a cuff it finds the bracer. On the
 * `_new_v3` batch it disagreed with Allen's eyes on all three known-bad hands. So
 * the proposal is drawn as a ghost the person adopts or overrides, and it refuses
 * to propose at all when the girth profile does not read as a tube narrowing to a
 * neck.
 *
 * Fail-loud (ADR-010 §7, ADR-025 §7): a station that would put the joint proximal
 * of the forearm's midpoint or distal beyond the hand's reach is a producer bug or
 * a coordinate from another model — refused by name. Everything is measured in
 * fractions of the limb length so a rescaled rig reads the same.
 *
 * Depends on: math/vec3. Depended on by: run-document (the move + the ownership
 * set), the hands panel (the ghost).
 */
import { KernelError } from '../ir/errors.js';
import { vec3Add, vec3Dot, vec3Length, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';

/** A cannot-place condition for the station — a producer/caller bug, fail-loud. */
export class WristStationRefusal extends KernelError {
  constructor(readonly reason: string) {
    super(`wrist station: ${reason}`);
  }
}

/** Below this many vertices a slice cannot name a girth. */
export const STATION_MIN_SLICE_SAMPLES = 20;
/** Axial slice thickness for the girth profile, as a fraction of limb length. */
export const STATION_SLICE_FRACTION = 0.04;
/**
 * HALF-thickness of the seat slab about the station's plane, as a fraction of
 * limb length — the section `wristSeatUnderPin` reads. Deliberately twice the
 * girth slice: the girth profile only needs a ring's WIDTH, while the seat
 * needs enough of a ring to run a covariance and cast a chord through it, and
 * generated wrists are coarse. MEASURED 2026-08-25 (Codex P1, #111): at ±0.04L
 * the seaBee Jay's wrist ring holds THREE vertices and the seat refuses; at
 * ±0.08L (a 120 mm forearm → a 19 mm slab) it holds a ring on every specimen
 * in the corpus. Do not shrink this without re-running the wrist-seat probe on
 * a low-poly hand — the coarse-ring test below is the gate.
 */
export const STATION_SEAT_SLAB_HALF_FRACTION = 0.08;
/**
 * Link distance for the seat slab's connected cluster, as a fraction of limb
 * length. The slab spans the whole scene at that plane (an A-pose thigh can
 * sit in it), so the wrist ring is the cluster CONNECTED to the click; the
 * link must exceed the ring's own vertex spacing or the cluster collapses to
 * the seed. MEASURED on the same specimen: at 0.10L Jay's R_Hand cluster
 * holds three vertices; 0.20L holds the ring.
 */
export const STATION_SEAT_LINK_FRACTION = 0.2;
/** How far PROXIMAL of the joint the crease may sit, in L. */
export const STATION_SEARCH_PROXIMAL = 0.3;
/**
 * How far DISTAL. Nearly nothing, on purpose (amended by the batch run,
 * 2026-08-15): the failure class this ADR exists for is the joint OVERSHOOTING
 * into the palm, so the crease is proximal of it. Searched symmetrically, the
 * proposer read the palm base of three low-poly hands (Darazin R/L +32/+20%L,
 * Cairn L +24%L) as a neck narrower than the cuff — a wrong-direction ghost on
 * exactly the hands that have the problem. A joint proximal of its own crease
 * has not been observed on this generator (hand weight starts AT the joint on
 * every batch hand); if it appears, it appears as a click.
 */
export const STATION_SEARCH_DISTAL = 0.05;
/** Perpendicular capture for the girth profile — beyond this is torso, not arm. */
export const STATION_CAPTURE_RADIUS_FRACTION = 0.6;
/**
 * The neck must be at least this much narrower than the wider of its two flanks
 * (±0.2L) to read as a wrist rather than as noise on a straight sleeve.
 */
export const STATION_NECK_CONTRAST = 0.88;
/** A confirmed station may not move the joint proximal of the forearm midpoint … */
export const STATION_MAX_PROXIMAL_L = 0.5;
/** … nor distal beyond this much of the limb (well short of any knuckle row). */
export const STATION_MAX_DISTAL_L = 0.6;

export interface WristStationProposal {
  /** Along-axis offset of the crease from the joint, in world units (negative = proximal). */
  readonly along: number;
  /** The same, as a fraction of limb length — the number a person reads. */
  readonly alongFraction: number;
  /** A point ON the arm's axis at that station (joint moved along only) — where the ghost draws. */
  readonly at: Vec3Data;
  /** RMS perpendicular radius at the neck and at the wider flank — the evidence. */
  readonly neckRadius: number;
  readonly flankRadius: number;
  readonly sampled: number;
}

/**
 * Measure the wrist crease along the arm as a ghost proposal (D1). `axis` need not
 * be normalised; `limbLength` scales every capture. Returns `null` when the girth
 * profile does not read as a neck — under a bracer, a cuff, a sleeve — which is a
 * product outcome the caller reports (`wrist-station-unmeasured`) and answers with
 * the click, never a silent keep-the-joint-as-if-measured.
 */
export function proposeWristStation(
  vertices: readonly Vec3Data[],
  joint: Vec3Data,
  axis: Vec3Data,
  limbLength: number,
): WristStationProposal | null {
  const axisLength = vec3Length(axis);
  if (!(axisLength > 0)) throw new WristStationRefusal('the limb axis has no direction — parent and child are co-located');
  if (!(limbLength > 0) || !Number.isFinite(limbLength)) {
    throw new WristStationRefusal(`limb length ${limbLength} is not a positive measurement`);
  }
  const unit = vec3Scale(axis, 1 / axisLength);
  const slice = STATION_SLICE_FRACTION * limbLength;
  const capture = STATION_CAPTURE_RADIUS_FRACTION * limbLength;
  const proximalSpan = STATION_SEARCH_PROXIMAL * limbLength;
  const distalSpan = STATION_SEARCH_DISTAL * limbLength;
  // Girth profile: rms perpendicular radius per axial slice, joint-relative.
  const sums = new Map<number, { s2: number; n: number }>();
  for (const v of vertices) {
    const d = vec3Sub(v, joint);
    const along = vec3Dot(d, unit);
    if (along < -(proximalSpan + 0.2 * limbLength) || along > distalSpan + 0.2 * limbLength) continue; // flanks need 0.2L beyond the span
    const r = vec3Length(vec3Sub(d, vec3Scale(unit, along)));
    if (r > capture) continue;
    const k = Math.round(along / slice);
    const cell = sums.get(k) ?? { s2: 0, n: 0 };
    cell.s2 += r * r;
    cell.n += 1;
    sums.set(k, cell);
  }
  const radiusAt = (k: number): number | null => {
    const c = sums.get(k);
    return c === undefined || c.n < STATION_MIN_SLICE_SAMPLES ? null : Math.sqrt(c.s2 / c.n);
  };
  const kProximal = Math.round(proximalSpan / slice);
  const kDistal = Math.round(distalSpan / slice);
  let bestK: number | null = null;
  let bestR = Infinity;
  let bestN = 0;
  for (let k = -kProximal; k <= kDistal; k += 1) {
    const r = radiusAt(k);
    if (r === null) continue;
    if (r < bestR) {
      bestR = r;
      bestK = k;
      bestN = sums.get(k)!.n;
    }
  }
  if (bestK === null) return null;
  // A neck is narrower than its flanks. Read the wider flank at ±0.2L; a slice
  // with too little flesh there means the arm ends (a wrist at the mesh edge) or
  // the capture missed — either way, no tube to name a neck in.
  const flankK = Math.round((0.2 * limbLength) / slice);
  const proximal = radiusAt(bestK - flankK);
  const distal = radiusAt(bestK + flankK);
  // BOTH flanks, or nothing (Codex P2, #97): with one flank missing the other
  // alone can pass the contrast test, and the end of a tapered limb — a
  // cropped mesh, a sparse cuff — reads as a neck. A neck has flesh on both
  // sides by definition.
  if (proximal === null || distal === null) return null;
  const flank = Math.max(proximal, distal);
  if (!(bestR <= STATION_NECK_CONTRAST * flank)) return null;
  const along = bestK * slice;
  return {
    along,
    alongFraction: along / limbLength,
    at: vec3Add(joint, vec3Scale(unit, along)),
    neckRadius: bestR,
    flankRadius: flank,
    sampled: bestN,
  };
}

/**
 * The joint's new position for a CONFIRMED station: the clicked point's along-axis
 * coordinate, applied to the joint along the arm only. Lateral placement is the
 * recentre's job and runs after this, about the moved joint. Refuses a station
 * that would leave the arm (§7).
 */
export function wristStationMove(
  joint: Vec3Data,
  axis: Vec3Data,
  limbLength: number,
  station: Vec3Data,
  /**
   * How far off the arm's axis a station may sit before it is a coordinate
   * from somewhere else (Codex P2, #97): only the along-axis component is
   * USED, so without this a stale point from another model with a plausible
   * axial projection would be silently projected onto this arm and accepted.
   * Pass the hand's own reach; default one limb length.
   */
  maxPerpendicular: number = limbLength,
): { readonly position: Vec3Data; readonly along: number; readonly alongFraction: number } {
  const axisLength = vec3Length(axis);
  if (!(axisLength > 0)) throw new WristStationRefusal('the limb axis has no direction — parent and child are co-located');
  if (!(limbLength > 0) || !Number.isFinite(limbLength)) {
    throw new WristStationRefusal(`limb length ${limbLength} is not a positive measurement`);
  }
  for (const [name, p] of [['joint', joint], ['station', station]] as const) {
    if (![p.x, p.y, p.z].every(Number.isFinite)) throw new WristStationRefusal(`${name} is not a finite point`);
  }
  const unit = vec3Scale(axis, 1 / axisLength);
  const offset = vec3Sub(station, joint);
  const along = vec3Dot(offset, unit);
  const perpendicular = vec3Length(vec3Sub(offset, vec3Scale(unit, along)));
  if (!(maxPerpendicular > 0) || !Number.isFinite(maxPerpendicular)) {
    throw new WristStationRefusal(`maxPerpendicular ${maxPerpendicular} is not a positive distance`);
  }
  if (perpendicular > maxPerpendicular) {
    throw new WristStationRefusal(
      `station sits ${(perpendicular / limbLength).toFixed(2)} forearm-lengths OFF the arm's axis (limit ${(maxPerpendicular / limbLength).toFixed(2)}) — ` +
        `that is not this arm; the specimen has probably changed since it was placed. Re-click the crease.`,
    );
  }
  const f = along / limbLength;
  if (f < -STATION_MAX_PROXIMAL_L) {
    throw new WristStationRefusal(
      `station sits ${(-f * 100).toFixed(0)}% of the forearm PROXIMAL of the joint (limit ${STATION_MAX_PROXIMAL_L * 100}%) — ` +
        `that is the forearm, not a wrist; re-click the crease`,
    );
  }
  if (f > STATION_MAX_DISTAL_L) {
    throw new WristStationRefusal(
      `station sits ${(f * 100).toFixed(0)}% of the forearm DISTAL of the joint (limit ${STATION_MAX_DISTAL_L * 100}%) — ` +
        `that is the hand, not a wrist; re-click the crease`,
    );
  }
  return { position: vec3Add(joint, vec3Scale(unit, along)), along, alongFraction: f };
}

export interface OwnedVertex {
  readonly index: number;
  readonly position: Vec3Data;
  /** Skeleton bone index of the vertex's dominant weight, or -1 when unweighted. */
  readonly dominantBone: number;
}

/**
 * The palm root, re-owned (D2, the wide rule): every vertex DISTAL of the
 * confirmed station (a slice of tolerance allowed) whose dominant bone is in the
 * severed forearm chain — twist helpers included — and which sits within `reach`
 * of the arm axis. Distal of the STATION, not merely between the stations: the
 * flesh Tripo gave the forearm is not only the palm root between the true wrist
 * and the dropped joint — Cairn's twist-owned thumb lobe reaches 26 mm PAST the
 * joint. The wrist is the boundary authority (the reclaim's own rule, ADR-024
 * era), now keyed to the wrist a person confirmed rather than to the joint that
 * was wrong. Returned as SOURCE vertex indices for `isolateHandRegion`'s `owned`
 * door. Only meaningful once a station is CONFIRMED (D4): the person declared
 * this hand a problem, so the cuff fuzz that moved golden baselines when a
 * heuristic reclaimed it unasked is theirs to re-own here.
 */
export function reownedVertices(
  vertices: readonly OwnedVertex[],
  station: Vec3Data,
  axis: Vec3Data,
  limbLength: number,
  forearmChain: ReadonlySet<number>,
  reach: number,
): Set<number> {
  const axisLength = vec3Length(axis);
  if (!(axisLength > 0)) throw new WristStationRefusal('the limb axis has no direction — parent and child are co-located');
  if (!(reach > 0) || !Number.isFinite(reach)) throw new WristStationRefusal(`reach ${reach} is not a positive distance`);
  const unit = vec3Scale(axis, 1 / axisLength);
  const lo = -STATION_SLICE_FRACTION * limbLength;
  const owned = new Set<number>();
  for (const v of vertices) {
    if (v.dominantBone < 0 || !forearmChain.has(v.dominantBone)) continue;
    const d = vec3Sub(v.position, station);
    const along = vec3Dot(d, unit);
    if (along < lo) continue; // the cuff — legitimately the forearm's
    if (along > reach) continue; // not this hand
    if (vec3Length(vec3Sub(d, vec3Scale(unit, along))) > reach) continue;
    owned.add(v.index);
  }
  return owned;
}
